import React, { useState, useEffect, useCallback } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/contexts/I18nContext";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

const KEY_HIDDEN = "globo:coach-hidden";

/**
 * Coach Globo — floating assistant.
 * Provides contextual tips per route (scripted responses).
 * Architecture-ready to plug in a real conversational AI later.
 */
export function CoachGlobo() {
  const { t, lang } = useI18n();
  const location = useLocation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);

  // Listen for programmatic open (e.g., from HelpCenter quick-card)
  useEffect(() => {
    const openCoach = () => setOpen(true);
    window.addEventListener("globo:coach-open", openCoach);
    return () => window.removeEventListener("globo:coach-open", openCoach);
  }, []);

  const greetingFor = useCallback(() => {
    const path = location.pathname;
    const name = user?.name?.split(" ")[0] || "";
    if (path === "/dashboard") return t("coach.greet.dashboard").replace("{name}", name);
    if (path === "/play") return t("coach.greet.lobby");
    if (path.startsWith("/game/")) return t("coach.greet.game");
    if (path === "/leaderboards") return t("coach.greet.lb");
    if (path === "/stats") return t("coach.greet.stats");
    if (path === "/rewards") return t("coach.greet.rewards");
    if (path === "/tutorial") return t("coach.greet.tutorial");
    if (path === "/demo") return t("coach.greet.demo");
    return t("coach.greet.default").replace("{name}", name);
  }, [location.pathname, user?.name, t]);

  useEffect(() => {
    if (open && msgs.length === 0) {
      setMsgs([{ role: "coach", text: greetingFor(), at: Date.now() }]);
    }
  }, [open, greetingFor, msgs.length]);

  // Reset messages when route changes
  useEffect(() => {
    if (open) {
      setMsgs([{ role: "coach", text: greetingFor(), at: Date.now() }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const send = async () => {
    const q = input.trim();
    if (!q) return;
    setMsgs((m) => [...m, { role: "user", text: q, at: Date.now() }]);
    setInput("");
    setTyping(true);
    try {
      const { data } = await api.post("/coach/chat", {
        message: q,
        language: lang,
      });
      setMsgs((m) => [...m, { role: "coach", text: data.reply, at: Date.now() }]);
    } catch (e) {
      // Fallback to local keyword matcher on error
      const reply = matchReply(q, lang, t);
      setMsgs((m) => [...m, { role: "coach", text: reply, at: Date.now() }]);
    } finally {
      setTyping(false);
    }
  };

  if (localStorage.getItem(KEY_HIDDEN) === "1" && !open) return null;
  // Only show on authenticated app routes
  if (!user) return null;

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-4 right-4 z-40 w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-2xl flex items-center justify-center hover:scale-105 transition-transform animate-float-in"
          data-testid="coach-globo-fab"
          aria-label="Coach Globo"
        >
          <div className="relative">
            <Sparkles className="w-6 h-6" />
            <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-success animate-pulse" />
          </div>
        </button>
      )}

      {open && (
        <div
          className="fixed bottom-4 right-4 z-40 w-[min(400px,calc(100vw-32px))] h-[520px] max-h-[70vh] bg-card border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-float-in"
          data-testid="coach-globo-panel"
        >
          <div className="flex items-center justify-between p-4 border-b border-border bg-gradient-to-r from-primary/10 to-transparent">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
                <Sparkles className="w-5 h-5" />
              </div>
              <div>
                <div className="font-display font-bold text-sm">{t("coach.name")}</div>
                <div className="text-xs text-muted-foreground flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-success" /> {t("coach.online")}
                </div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-muted-foreground hover:text-foreground p-1"
              data-testid="coach-close-btn"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {msgs.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                data-testid={`coach-msg-${m.role}-${i}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-primary text-primary-foreground rounded-br-sm"
                      : "bg-secondary text-secondary-foreground rounded-bl-sm"
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {typing && (
              <div className="flex justify-start" data-testid="coach-typing">
                <div className="bg-secondary rounded-2xl px-4 py-2.5 text-sm inline-flex gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            )}
          </div>

          <div className="p-3 border-t border-border">
            <div className="flex flex-wrap gap-1.5 mb-3">
              {[
                t("coach.q.rules"),
                t("coach.q.scores"),
                t("coach.q.play"),
              ].map((q) => (
                <button
                  key={q}
                  onClick={() => { setInput(q); setTimeout(() => send(), 50); }}
                  className="text-xs px-2.5 py-1 rounded-full border border-border hover:bg-secondary transition-colors"
                  data-testid={`coach-suggest-${q.slice(0, 15)}`}
                >
                  {q}
                </button>
              ))}
            </div>
            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="flex gap-2"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t("coach.placeholder")}
                className="flex-1 h-10 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:border-primary"
                data-testid="coach-input"
              />
              <Button size="sm" type="submit" data-testid="coach-send-btn">
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

function matchReply(q, lang, t) {
  const norm = q.toLowerCase();
  if (/règle|rules|comment jouer|how to play/.test(norm)) return t("coach.a.rules");
  if (/score|point|calcul/.test(norm)) return t("coach.a.scores");
  if (/plateau|board|case|cell/.test(norm)) return t("coach.a.board");
  if (/joker|blank/.test(norm)) return t("coach.a.blank");
  if (/bonus|émerg|emerg/.test(norm)) return t("coach.a.bonus");
  if (/badge|récomp|reward/.test(norm)) return t("coach.a.rewards");
  if (/temps|timer|chrono/.test(norm)) return t("coach.a.timer");
  if (/passer|échang|pass|exchange/.test(norm)) return t("coach.a.pass");
  if (/hors ligne|offline|install/.test(norm)) return t("coach.a.pwa");
  return t("coach.a.default");
}
